import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Clock, ArrowUp, ChevronRight } from "lucide-react";
import { CONTACT, LOGO } from "@/data/content";
import { scrollToId } from "@/components/SmoothScroll";
import Magnetic from "@/components/Magnetic";

const NAV = [
    { label: "Accueil", to: "/#accueil" },
    { label: "Services", to: "/#services" },
    { label: "Avant / Après", to: "/#avant-apres" },
    { label: "Avis", to: "/#avis" },
    { label: "Contact", to: "/#contact" },
];
const SPEC = [
    { label: "Syndrome de Diogène", to: "/nettoyage-syndrome-diogene-strasbourg" },
    { label: "Nettoyage après décès", to: "/nettoyage-apres-deces-strasbourg" },
];

export default function Footer() {
    const year = new Date().getFullYear();
    return (
        <footer className="bg-ink text-white/80 font-body" data-testid="footer">
            {/* Top section */}
            <div className="max-w-7xl mx-auto px-6 md:px-12 pt-20 pb-14">
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
                    {/* Brand */}
                    <div className="lg:col-span-1">
                        <img src={LOGO} alt="IGN Services Alsace" className="h-14 w-auto mb-5" data-testid="footer-logo" />
                        <p className="text-sm leading-relaxed text-white/60 mb-4">
                            Entreprise de nettoyage professionnel et d&apos;hygiène à Strasbourg et en Alsace. Entretien, désinfection, vitrerie et remise en état — exigence terrain, résultats visibles.
                        </p>
                        <p className="text-sm font-display italic text-electric-soft">
                            On redonne de la valeur à vos espaces.
                        </p>
                    </div>

                    {/* Navigation */}
                    <div>
                        <h4 className="text-xs uppercase tracking-[0.2em] text-white font-semibold mb-5">Navigation</h4>
                        <ul className="space-y-2.5">
                            {NAV.map((n) => (
                                <li key={n.label}>
                                    <Link to={n.to} className="group flex items-center gap-1.5 text-sm text-white/55 hover:text-white transition-colors">
                                        <ChevronRight className="w-3 h-3 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                                        {n.label}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Specialized */}
                    <div>
                        <h4 className="text-xs uppercase tracking-[0.2em] text-white font-semibold mb-5">Spécialisations</h4>
                        <ul className="space-y-2.5">
                            {SPEC.map((s) => (
                                <li key={s.label}>
                                    <Link to={s.to} className="group flex items-center gap-1.5 text-sm text-white/55 hover:text-white transition-colors">
                                        <ChevronRight className="w-3 h-3 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                                        {s.label}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Contact */}
                    <div>
                        <h4 className="text-xs uppercase tracking-[0.2em] text-white font-semibold mb-5">Contact</h4>
                        <ul className="space-y-3">
                            <li className="flex items-start gap-3 text-sm text-white/55">
                                <MapPin className="w-4 h-4 mt-0.5 text-electric-soft shrink-0" />
                                <span>{CONTACT.address}</span>
                            </li>
                            <li>
                                <a href={`tel:${CONTACT.phone}`} className="flex items-center gap-3 text-sm text-white/55 hover:text-white transition-colors">
                                    <Phone className="w-4 h-4 text-electric-soft" />
                                    {CONTACT.phone}
                                </a>
                            </li>
                            <li>
                                <a href={`mailto:${CONTACT.email}`} className="flex items-center gap-3 text-sm text-white/55 hover:text-white transition-colors">
                                    <Mail className="w-4 h-4 text-electric-soft" />
                                    {CONTACT.email}
                                </a>
                            </li>
                            <li className="flex items-center gap-3 text-sm text-white/55">
                                <Clock className="w-4 h-4 text-electric-soft" />
                                <span>{CONTACT.hours}</span>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>

            {/* Bottom bar */}
            <div className="border-t border-white/10">
                <div className="max-w-7xl mx-auto px-6 md:px-12 py-6 flex flex-col md:flex-row items-center justify-between gap-4">
                    <p className="text-xs text-white/40">© {year} IGN Services Alsace — Tous droits réservés</p>
                    <Magnetic strength={0.2}>
                        <button
                            onClick={() => scrollToId("accueil")}
                            className="group flex items-center gap-2 text-xs uppercase tracking-wider text-white/40 hover:text-white transition-colors"
                            aria-label="Retour en haut"
                            data-testid="footer-back-to-top"
                        >
                            Haut de page
                            <ArrowUp className="w-3.5 h-3.5 group-hover:-translate-y-0.5 transition-transform" />
                        </button>
                    </Magnetic>
                </div>
            </div>
        </footer>
    );
}
